import { useState, useEffect } from 'react';

export interface LiveDemoSandboxProps {
  onWriteAction: (actionName: string) => void;
}

export function LiveDemoSandbox({ onWriteAction }: LiveDemoSandboxProps) {
  const [activeTab, setActiveTab] = useState<
    'overview' | 'applications' | 'notices' | 'consents' | 'apikeys' | 'settings'
  >('overview');

  // Live dynamic feed for audit events inside demo log
  const [auditLogs, setAuditLogs] = useState([
    { id: 1, type: 'grant', label: 'consent.granted', detail: 'dp_usr_48213 · privacy-v4.2 · app_01jf8', time: '09:41:22' },
    { id: 2, type: 'grant', label: 'consent.granted', detail: 'dp_usr_48212 · privacy-v4.2 · app_01jf8', time: '09:38:07' },
    { id: 3, type: 'notice', label: 'notice.published', detail: 'privacy-v4.2 · sha:a3f9c1b2 · admin@acme.in', time: '07-11 09:20' },
    { id: 4, type: 'key', label: 'api_key.created', detail: 'ck_live_...8f2a · scoped: write:consents', time: '07-11 09:12' },
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
      const randomUser = Math.floor(48214 + Math.random() * 50);

      setAuditLogs((prev) => [
        {
          id: Date.now(),
          type: 'grant',
          label: 'consent.granted',
          detail: `dp_usr_${randomUser} · privacy-v4.2 · app_01jf8`,
          time: timeStr,
        },
        ...prev.slice(0, 5),
      ]);
    }, 7000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section id="demo" className="relative py-28 px-6 lg:px-8">
      {/* Visual connector line */}
      <div className="sc" aria-hidden="true">
        <svg width="2" height="24" viewBox="0 0 2 24">
          <line x1="1" y1="0" x2="1" y2="24" className="sc-line" stroke="#2563EB" strokeOpacity="0.3" strokeWidth="1.5" />
        </svg>
        <span className="sc-node" />
      </div>

      <div className="max-w-3xl mx-auto text-center mb-12">
        <h2 className="text-[32px] sm:text-[40px] font-extrabold tracking-tight text-ink">
          Interactive Sandbox Demo
        </h2>
        <p className="mt-4 text-[16px] text-sub leading-relaxed">
          Explore a read-only preview of the Consentry dashboard workspace. Write operations require an account.
        </p>
      </div>

      <div className="max-w-5xl mx-auto rounded-xl3 border border-line shadow-cardhover overflow-hidden">
        {/* Sandbox Header */}
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-line bg-surface">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]"></span>
            <span className="ml-3 mono text-[11px] text-muted">app.consentry.io</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-ok"></span>
            <span className="mono text-[11px] text-muted">Read-only · Demo workspace</span>
          </div>
        </div>

        {/* Sandbox Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 demo-grid">
          {/* Sidebar Tabs */}
          <div className="demo-sidebar col-span-1 border-r border-line bg-card p-3 space-y-0.5">
            <div className="px-3 py-1.5 text-[10.5px] font-semibold text-muted uppercase tracking-wide">
              Workspace
            </div>

            <button
              type="button"
              onClick={() => setActiveTab('overview')}
              className={`dti dash-tab w-full text-left px-3 py-2.5 rounded-lg text-[13px] font-medium flex items-center gap-2.5 cursor-pointer ${
                activeTab === 'overview' ? 'active' : 'text-sub'
              }`}
            >
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Overview
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('applications')}
              className={`dti dash-tab w-full text-left px-3 py-2.5 rounded-lg text-[13px] font-medium flex items-center gap-2.5 cursor-pointer ${
                activeTab === 'applications' ? 'active' : 'text-sub'
              }`}
            >
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Applications
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('notices')}
              className={`dti dash-tab w-full text-left px-3 py-2.5 rounded-lg text-[13px] font-medium flex items-center gap-2.5 cursor-pointer ${
                activeTab === 'notices' ? 'active' : 'text-sub'
              }`}
            >
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Notices
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('consents')}
              className={`dti dash-tab w-full text-left px-3 py-2.5 rounded-lg text-[13px] font-medium flex items-center gap-2.5 cursor-pointer ${
                activeTab === 'consents' ? 'active' : 'text-sub'
              }`}
            >
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Consent Log
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('apikeys')}
              className={`dti dash-tab w-full text-left px-3 py-2.5 rounded-lg text-[13px] font-medium flex items-center gap-2.5 cursor-pointer ${
                activeTab === 'apikeys' ? 'active' : 'text-sub'
              }`}
            >
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 0121 9z" />
              </svg>
              API Keys
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('settings')}
              className={`dti dash-tab w-full text-left px-3 py-2.5 rounded-lg text-[13px] font-medium flex items-center gap-2.5 cursor-pointer ${
                activeTab === 'settings' ? 'active' : 'text-sub'
              }`}
            >
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              </svg>
              Settings
            </button>
          </div>

          {/* Panel Display Area */}
          <div className="col-span-3 p-5 sm:p-6 min-h-[420px] relative bg-card">
            {/* TAB 1: OVERVIEW */}
            {activeTab === 'overview' && (
              <div className="animate-log-slide">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-[15px] font-semibold text-ink">Overview</h3>
                    <p className="mono text-[11px] text-muted mt-0.5">as of 2026-07-25 · 22:50:00 IST</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onWriteAction('Generate API Key')}
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium bg-ink text-surface px-3.5 py-2 rounded-xl hover:opacity-80 transition cursor-pointer"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 0121 9z" />
                    </svg>
                    Generate API Key
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="border border-line rounded-xl p-4 bg-surface">
                    <div className="text-[11.5px] text-muted mb-1.5">Total consents</div>
                    <div className="text-[24px] font-bold mono text-ink">482,910</div>
                    <div className="text-[11px] text-ok mono mt-1">↑ 2,140 today</div>
                  </div>
                  <div className="border border-line rounded-xl p-4 bg-surface">
                    <div className="text-[11.5px] text-muted mb-1.5">Active applications</div>
                    <div className="text-[24px] font-bold mono text-ink">6</div>
                    <div className="text-[11px] text-muted mono mt-1">3 regions</div>
                  </div>
                  <div className="border border-line rounded-xl p-4 bg-surface">
                    <div className="text-[11.5px] text-muted mb-1.5">Certificates issued</div>
                    <div className="text-[24px] font-bold mono text-ink">128</div>
                    <div className="text-[11px] text-ok mono mt-1">Q2 2026 current</div>
                  </div>
                </div>

                <div className="border border-line rounded-xl p-4 bg-surface">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11.5px] text-muted">Consent events — last 7 days</span>
                    <span className="text-[11px] font-medium text-ok">↑ 12.4% vs prior week</span>
                  </div>
                  <div className="h-28 flex items-end gap-2">
                    <div className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-blue-100 dark:bg-blue-900/40 rounded-sm" style={{ height: '35px' }}></div>
                      <span className="mono text-[9px] text-muted">Mon</span>
                    </div>
                    <div className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-blue-100 dark:bg-blue-900/40 rounded-sm" style={{ height: '52px' }}></div>
                      <span className="mono text-[9px] text-muted">Tue</span>
                    </div>
                    <div className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-blue-100 dark:bg-blue-900/40 rounded-sm" style={{ height: '44px' }}></div>
                      <span className="mono text-[9px] text-muted">Wed</span>
                    </div>
                    <div className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-blue-100 dark:bg-blue-900/40 rounded-sm" style={{ height: '68px' }}></div>
                      <span className="mono text-[9px] text-muted">Thu</span>
                    </div>
                    <div className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-blue rounded-sm" style={{ height: '88px' }}></div>
                      <span className="mono text-[9px] text-blue font-medium">Fri</span>
                    </div>
                    <div className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-blue-100 dark:bg-blue-900/40 rounded-sm" style={{ height: '61px' }}></div>
                      <span className="mono text-[9px] text-muted">Sat</span>
                    </div>
                    <div className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-blue-100 dark:bg-blue-900/40 rounded-sm" style={{ height: '74px' }}></div>
                      <span className="mono text-[9px] text-muted">Sun</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: APPLICATIONS */}
            {activeTab === 'applications' && (
              <div className="animate-log-slide">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-[15px] font-semibold text-ink">Applications</h3>
                  <button
                    type="button"
                    onClick={() => onWriteAction('Create Application')}
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium bg-blue text-white px-3.5 py-2 rounded-xl hover:opacity-90 transition cursor-pointer"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Create Application
                  </button>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between border border-line rounded-xl px-4 py-3 bg-surface hover:border-blue/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <svg className="w-4 h-4 text-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <div className="text-[13.5px] font-medium text-ink">FinTech Consumer App</div>
                        <div className="mono text-[11px] text-muted">app_01jf8a91c2b</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="mono text-[11px] text-muted">48,210 consents</span>
                      <span className="text-[11px] font-medium text-ok bg-[#F0FDF4] dark:bg-emerald-950/40 px-2 py-1 rounded-full">
                        Live
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between border border-line rounded-xl px-4 py-3 bg-surface hover:border-blue/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <svg className="w-4 h-4 text-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                      <div>
                        <div className="text-[13.5px] font-medium text-ink">Patient Portal — Web</div>
                        <div className="mono text-[11px] text-muted">app_02ka4819d4e</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="mono text-[11px] text-muted">12,890 consents</span>
                      <span className="text-[11px] font-medium text-ok bg-[#F0FDF4] dark:bg-emerald-950/40 px-2 py-1 rounded-full">
                        Live
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between border border-line rounded-xl px-4 py-3 bg-surface hover:border-blue/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <svg className="w-4 h-4 text-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                      </svg>
                      <div>
                        <div className="text-[13.5px] font-medium text-ink">Internal Admin Dashboard</div>
                        <div className="mono text-[11px] text-muted">app_03sb9001f3a</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="mono text-[11px] text-muted">Testing</span>
                      <span className="text-[11px] font-medium text-warn bg-[#FFFBEB] dark:bg-amber-950/40 px-2 py-1 rounded-full">
                        Staging
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 3: NOTICES */}
            {activeTab === 'notices' && (
              <div className="animate-log-slide">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-[15px] font-semibold text-ink">Notices</h3>
                  <button
                    type="button"
                    onClick={() => onWriteAction('Publish Notice')}
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium bg-blue text-white px-3.5 py-2 rounded-xl hover:opacity-90 transition cursor-pointer"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Publish Notice
                  </button>
                </div>

                <div className="space-y-2.5">
                  <div className="border border-line rounded-xl px-4 py-3.5 bg-surface">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[13.5px] font-medium text-ink">Privacy Notice — v4.2</div>
                      <span className="text-[11px] font-medium text-ok bg-[#F0FDF4] dark:bg-emerald-950/40 px-2 py-1 rounded-full">
                        Active
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-[11.5px] text-muted mono">
                      <div>
                        <span className="block text-[10px] mb-0.5 uppercase tracking-wide">Hash</span>
                        sha:a3f9c1b2
                      </div>
                      <div>
                        <span className="block text-[10px] mb-0.5 uppercase tracking-wide">Published</span>
                        2026-07-11
                      </div>
                      <div>
                        <span className="block text-[10px] mb-0.5 uppercase tracking-wide">Languages</span>
                        EN / HI / MR
                      </div>
                    </div>
                  </div>

                  <div className="border border-line rounded-xl px-4 py-3.5 bg-surface">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[13.5px] font-medium text-ink">Marketing Consent — v2.0</div>
                      <span className="text-[11px] font-medium text-ok bg-[#F0FDF4] dark:bg-emerald-950/40 px-2 py-1 rounded-full">
                        Active
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-[11.5px] text-muted mono">
                      <div>
                        <span className="block text-[10px] mb-0.5 uppercase tracking-wide">Hash</span>
                        sha:b7e2049c
                      </div>
                      <div>
                        <span className="block text-[10px] mb-0.5 uppercase tracking-wide">Published</span>
                        2026-07-01
                      </div>
                      <div>
                        <span className="block text-[10px] mb-0.5 uppercase tracking-wide">Languages</span>
                        EN / HI
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 4: CONSENT LOG */}
            {activeTab === 'consents' && (
              <div className="animate-log-slide">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-[15px] font-semibold text-ink">Consent Records</h3>
                    <p className="mono text-[11px] text-muted mt-0.5">Append-only · No UPDATE or DELETE possible</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onWriteAction('Export Consent Log')}
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-line text-sub px-3.5 py-2 rounded-xl hover:bg-slate-500/10 transition cursor-pointer"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Export Log
                  </button>
                </div>

                <div className="space-y-1.5" id="audit-feed">
                  {auditLogs.map((log) => (
                    <div
                      key={log.id}
                      className="log-entry border border-line rounded-xl px-4 py-3 flex items-start gap-3 bg-surface"
                    >
                      <svg className="w-3.5 h-3.5 text-emerald mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-medium text-ink">{log.label}</div>
                        <div className="mono text-[11px] text-muted">{log.detail}</div>
                      </div>
                      <span className="mono text-[10.5px] text-muted shrink-0">{log.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB 5: API KEYS */}
            {activeTab === 'apikeys' && (
              <div className="animate-log-slide">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-[15px] font-semibold text-ink">API Keys</h3>
                  <button
                    type="button"
                    onClick={() => onWriteAction('Create API Key')}
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium bg-blue text-white px-3.5 py-2 rounded-xl hover:opacity-90 transition cursor-pointer"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Create API Key
                  </button>
                </div>

                <div className="space-y-3">
                  <div className="border border-line rounded-xl p-4 bg-surface">
                    <div className="flex items-center justify-between mb-2">
                      <span className="mono text-xs font-semibold text-ink">ck_live_••••8f2a</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold text-ok bg-[#F0FDF4] dark:bg-emerald-950/40 px-2 py-0.5 rounded-full">
                          Live
                        </span>
                        <button
                          type="button"
                          onClick={() => onWriteAction('Revoke API Key')}
                          className="text-xs text-err hover:underline cursor-pointer"
                        >
                          Revoke
                        </button>
                      </div>
                    </div>
                    <div className="text-[11.5px] text-muted space-y-0.5 mono">
                      <div>Scope: write:consents, read:notices</div>
                      <div>Created: 2026-07-11 09:12 IST by admin@acme.in</div>
                    </div>
                  </div>

                  <div className="border border-line rounded-xl p-4 bg-surface">
                    <div className="flex items-center justify-between mb-2">
                      <span className="mono text-xs font-semibold text-ink">ck_test_••••3b9c</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold text-warn bg-[#FFFBEB] dark:bg-amber-950/40 px-2 py-0.5 rounded-full">
                          Test
                        </span>
                        <button
                          type="button"
                          onClick={() => onWriteAction('Revoke API Key')}
                          className="text-xs text-err hover:underline cursor-pointer"
                        >
                          Revoke
                        </button>
                      </div>
                    </div>
                    <div className="text-[11.5px] text-muted space-y-0.5 mono">
                      <div>Scope: full_access</div>
                      <div>Created: 2026-07-01 14:05 IST by dev@acme.in</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 6: SETTINGS */}
            {activeTab === 'settings' && (
              <div className="animate-log-slide space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-[15px] font-semibold text-ink">Organisation Settings</h3>
                  <button
                    type="button"
                    onClick={() => onWriteAction('Save Settings')}
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium bg-ink text-surface px-3.5 py-2 rounded-xl hover:opacity-80 transition cursor-pointer"
                  >
                    Save Changes
                  </button>
                </div>

                <div className="border border-line rounded-xl p-4 bg-surface space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-sub uppercase mb-1">
                      Organisation Name
                    </label>
                    <input
                      type="text"
                      readOnly
                      value="Acme Fiduciary Pvt Ltd"
                      className="w-full bg-card border border-line rounded-lg px-3 py-2 text-xs text-ink cursor-not-allowed opacity-90"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-sub uppercase mb-1">
                        Primary Region
                      </label>
                      <input
                        type="text"
                        readOnly
                        value="Mumbai (ap-south-1)"
                        className="w-full bg-card border border-line rounded-lg px-3 py-2 text-xs text-ink cursor-not-allowed opacity-90"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-sub uppercase mb-1">
                        Data Retention
                      </label>
                      <input
                        type="text"
                        readOnly
                        value="7 Years (DPDP Requirement)"
                        className="w-full bg-card border border-line rounded-lg px-3 py-2 text-xs text-ink cursor-not-allowed opacity-90"
                      />
                    </div>
                  </div>
                </div>

                <div className="border border-line rounded-xl p-4 bg-surface flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold text-ink">Webhooks & Event Propagation</div>
                    <div className="text-[11px] text-muted">Automatically push withdrawal events to downstream CRM/DB.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onWriteAction('Add Webhook Endpoint')}
                    className="text-xs font-semibold text-blue border border-blue/30 rounded-lg px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition cursor-pointer"
                  >
                    + Add Endpoint
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
